import { Search, LayoutGrid, List } from "lucide-react";
import PropTypes from "prop-types";
import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import FilterDropdown from "./FilterDropdown";

const FilterBar = ({
  filters,
  activeFilter,
  setActiveFilter,
  viewMode,
  setViewMode,
  onSearch,
  onAdvancedFiltersChange,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [, setActiveAdvancedFilters] = useState([]);

  const handleSearch = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const submitSearch = useCallback(() => {
    onSearch(searchQuery);
  }, [searchQuery, onSearch]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        submitSearch();
      }
    },
    [submitSearch]
  );

  const handleFilterClick = useCallback(
    (filter) => {
      setActiveFilter(filter);
    },
    [setActiveFilter]
  );

  const handleViewModeChange = useCallback(
    (mode) => {
      setViewMode(mode);
    },
    [setViewMode]
  );

  const handleAdvancedFiltersChange = useCallback(
    (filters) => {
      setActiveAdvancedFilters(filters);
      onAdvancedFiltersChange(filters); // Pass filters to parent component
    },
    [onAdvancedFiltersChange]
  );

  return (
    <div className="flex flex-wrap items-center w-full lg:justify-between justify-center gap-4 p-4 border-b">
      {/* Filters Section */}
      <div className="flex items-center gap-2 justify-evenly w-full lg:w-auto flex-wrap">
        {filters.map((filter) => (
          <Button
            key={filter}
            variant={activeFilter === filter ? "default" : "outline"}
            className={`rounded-full text-sm ${
              activeFilter === filter
                ? "bg-orange-500 text-white hover:bg-orange-600"
                : "text-gray-400 hover:bg-gray-100"
            }`}
            onClick={() => handleFilterClick(filter)}
            aria-pressed={activeFilter === filter}
            aria-label={`Filter by ${filter}`}
          >
            {filter}
          </Button>
        ))}
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between w-full lg:w-auto gap-2 flex-wrap">
        {/* Search Input */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border flex-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 p-0"
            onClick={submitSearch}
            aria-label="Search"
          >
            <Search className="h-4 w-4 text-gray-400" />
          </Button>
          <input
            type="text"
            placeholder="Search by name or ID"
            className="w-full text-sm bg-transparent focus:outline-none"
            value={searchQuery}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            aria-label="Search appointments"
          />
        </div>

        {/* Advanced Filter Dropdown */}
        <FilterDropdown
          onFiltersChange={handleAdvancedFiltersChange}
          activeFilterStatus={activeFilter}
        />

        {/* View Mode Toggle (Grid/List) */}
        <div
          className="hidden md:flex items-center gap-1"
          role="radiogroup"
          aria-label="View mode"
        >
          <Button
            variant="ghost"
            size="icon"
            className={`h-10 w-10 rounded-full ${
              viewMode === "grid" ? "bg-gray-300" : "hover:bg-gray-100"
            }`}
            onClick={() => handleViewModeChange("grid")}
            aria-pressed={viewMode === "grid"}
            aria-label="Grid view"
          >
            <LayoutGrid className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-10 w-10 rounded-full ${
              viewMode === "list" ? "bg-gray-300" : "hover:bg-gray-100"
            }`}
            onClick={() => handleViewModeChange("list")}
            aria-pressed={viewMode === "list"}
            aria-label="List view"
          >
            <List className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

FilterBar.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeFilter: PropTypes.string.isRequired,
  setActiveFilter: PropTypes.func.isRequired,
  viewMode: PropTypes.string.isRequired,
  setViewMode: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onAdvancedFiltersChange: PropTypes.func,
};

export default FilterBar;
